import { Switch, TableCell, TableRow } from '@heroui/react';
import React, { useState } from 'react';
import { FiToggleLeft } from 'react-icons/fi';

import { useNotification } from '../../contexts/NotificationContext';
import { AlertCard } from '../AlertCard';
import { LoadingTable, type LoadingTableColumn } from '../Table/LoadingTable';

import { ConfirmProductionChangeModal } from './ConfirmProductionChangeModal';
import { getEntityDescriptor } from './entities';
import { FlagDescription } from './FlagDescription';
import {
  type FeatureFlagControlRow,
  useFeatureFlagControl,
} from './hooks/useFeatureFlagControl';
import { type FeatureFlagControlEntity } from './types';

export const DEFAULT_READ_TAG = 'portal-read';
export const DEFAULT_WRITE_TAG = 'portal-write';

export interface FeatureFlagControlTableProps {
  entity: FeatureFlagControlEntity;
  /** Shown in messages, e.g. the distributor's name. */
  entityName?: string;
  /** Flags must carry every one of these tags to be listed. */
  tags: string[];
  /** Flags carrying any of these tags are left out. */
  excludeTags?: string[];
  /** Flags with this tag are listed but can't be changed. */
  readTag?: string;
  /** Flags with this tag can be switched for this entity. */
  writeTag?: string;
}

const COLUMNS: LoadingTableColumn[] = [
  { label: 'Feature', width: '70%' },
  { label: 'Status', width: '30%' },
];

interface PendingChange {
  row: FeatureFlagControlRow;
  turnOn: boolean;
}

/**
 * Lists the features tagged for an entity page with whether this entity has
 * them, and turns one on or off for this entity only. Mount it on any entity
 * page by passing the entity and the tags that page cares about.
 */
export const FeatureFlagControlTable: React.FC<
  FeatureFlagControlTableProps
> = ({
  entity,
  entityName,
  tags,
  excludeTags,
  readTag = DEFAULT_READ_TAG,
  writeTag = DEFAULT_WRITE_TAG,
}) => {
  const { addNotification } = useNotification();
  const [pending, setPending] = useState<PendingChange>();
  const {
    rows,
    isLoading,
    errorMessage,
    hasNoAccess,
    isProduction,
    savingKey,
    setFlag,
  } = useFeatureFlagControl({
    entity,
    tags,
    excludeTags,
    readTag,
    writeTag,
  });

  const { label } = getEntityDescriptor(entity.kind);
  const name = entityName || `this ${label}`;

  if (hasNoAccess) {
    return (
      <AlertCard
        type="warning"
        title="No access"
        message="Only Choco admins can see and change these features."
      />
    );
  }

  if (errorMessage) {
    return (
      <AlertCard
        type="error"
        title="Could not load the features"
        message={errorMessage}
      />
    );
  }

  const save = async ({ row, turnOn }: PendingChange) => {
    const result = await setFlag(row.key, turnOn);
    setPending(undefined);

    if (result.kind === 'error') {
      addNotification({
        title: `${row.name} not changed`,
        message: result.message,
        type: 'error',
      });
      return;
    }

    if (result.isOn !== turnOn) {
      addNotification({
        title: `${row.name} saved`,
        message: `It is still ${result.isOn ? 'on' : 'off'} for ${name}.`,
        type: 'warning',
      });
      return;
    }

    addNotification({
      title: `${row.name} turned ${turnOn ? 'on' : 'off'}`,
      message: `${name} ${turnOn ? 'now has' : 'no longer has'} this feature.`,
      type: 'success',
    });
  };

  const handleToggle = (row: FeatureFlagControlRow, turnOn: boolean) => {
    if (isProduction) {
      setPending({ row, turnOn });
      return;
    }
    save({ row, turnOn });
  };

  const statusHint = (row: FeatureFlagControlRow): string | undefined => {
    if (row.control.kind === 'viewOnly') return 'View only';
    if (row.control.kind === 'locked') return row.control.reason;
    return undefined;
  };

  const renderRow = (row: FeatureFlagControlRow) => {
    const hint = statusHint(row);

    return (
      <TableRow key={row.key}>
        <TableCell className="align-top">
          <div className="py-1">
            <p className="font-medium text-foreground">{row.name}</p>
            <code className="text-xs text-default-400">{row.key}</code>
            {row.description && (
              <FlagDescription description={row.description} />
            )}
            {row.notes.length > 0 && (
              <ul className="mt-1 list-disc pl-4 text-xs text-default-400">
                {row.notes.map((note) => (
                  <li key={note}>{note}</li>
                ))}
              </ul>
            )}
          </div>
        </TableCell>
        <TableCell className="align-top">
          <div className="flex flex-col items-start gap-1 py-1">
            {row.isBoolean ? (
              <Switch
                size="sm"
                aria-label={`${row.name} for ${name}`}
                isSelected={row.isOn}
                isDisabled={
                  row.control.kind !== 'editable' || Boolean(savingKey)
                }
                onValueChange={(next) => handleToggle(row, next)}
              >
                {row.isOn ? 'On' : 'Off'}
              </Switch>
            ) : (
              <span className="text-sm text-foreground">{row.valueLabel}</span>
            )}
            {hint && <span className="text-xs text-default-400">{hint}</span>}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <LoadingTable
        ariaLabel={`Features for ${name}`}
        columns={COLUMNS}
        rowCount={3}
        isLoading={isLoading}
        loadingMessage="Loading features..."
        isEmpty={rows.length === 0}
        emptyState={{
          icon: FiToggleLeft,
          title: 'No features to show',
          description: `No features are set up to be managed for a ${label} yet.`,
        }}
        data={rows}
        renderRow={renderRow}
      />

      {pending && (
        <ConfirmProductionChangeModal
          isOpen
          onClose={() => setPending(undefined)}
          onConfirm={() => save(pending)}
          featureName={pending.row.name}
          entityName={name}
          turnOn={pending.turnOn}
          isSaving={savingKey === pending.row.key}
        />
      )}
    </>
  );
};
